import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Zap } from "lucide-react";
import { useCreateTimeProposal } from "@/lib/sessionQueries";
import { HOURS, MINUTES, BROWSER_TZ, todayLocalISODate, nowLocalHourMinute, toLocalDate, formatInTimezone } from "@/lib/localDateTime";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function emptySlot() {
  return { date: "", hour: "", minute: "", duration_minutes: 60 };
}

/**
 * Master v5 Prompt 18 — SessionTimeProposal composer. Used for BOTH a
 * fresh "propose a time" and a reschedule (pass `rescheduleOfSessionId`)
 * — TidyCal exposes no create/reschedule API, so accepting one of these
 * slots creates an internal 'pending_confirmation' Session, never a real
 * booking directly (see session_repository.py's module docstring).
 *
 * Master v5 Prompt 31 — deliberately NO minimum-date/lead-time rule: any
 * day, any time, including right now, is a valid candidate slot.
 */
export default function TimeProposalComposer({ workspaceId, ownerMemberId, rescheduleOfSessionId, onClose, onCreated }) {
  const [slots, setSlots] = useState([emptySlot()]);
  const [note, setNote] = useState("");
  const createProposal = useCreateTimeProposal(workspaceId);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function updateSlot(i, field, value) {
    setSlots((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  }
  function setRightNow(i) {
    const { hour, minute } = nowLocalHourMinute();
    setSlots((prev) => prev.map((s, idx) => (idx === i ? { ...s, date: todayLocalISODate(), hour, minute } : s)));
  }
  function addSlot() {
    if (slots.length >= 5) return;
    setSlots((prev) => [...prev, emptySlot()]);
  }
  function removeSlot(i) {
    setSlots((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSubmit() {
    const validSlots = slots.map((s) => ({ ...s, _date: toLocalDate(s) })).filter((s) => s._date);
    if (validSlots.length === 0) {
      toast.error("Add at least one candidate time — pick a date, hour and minute");
      return;
    }
    try {
      await createProposal.mutateAsync({
        owner_member_id: ownerMemberId,
        slots: validSlots.map((s) => ({ starts_at: s._date.toISOString(), duration_minutes: Number(s.duration_minutes) || 60 })),
        note: note || null,
        reschedule_of_session_id: rescheduleOfSessionId || null,
      });
      toast.success("Time proposal sent to the client");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't send this time proposal");
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4" data-testid="time-proposal-composer-modal">
      <div className="bg-paper rounded-xl border border-ink/10 max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl text-ink">{rescheduleOfSessionId ? "Propose a reschedule" : "Propose a time"}</h2>
          <button onClick={onClose} data-testid="time-proposal-composer-close-btn"><X size={18} className="text-ink/50" /></button>
        </div>
        <p className="text-xs text-ink-soft">
          Offer 1-5 candidate times — any day, any time, including right now. Times below are in your own local
          time ({BROWSER_TZ}); the client sees them converted to their own time automatically.
        </p>

        <div className="space-y-3">
          {slots.map((s, i) => {
            const preview = toLocalDate(s);
            return (
              <div key={i} className="border border-ink/10 rounded-lg p-2.5 space-y-1.5" data-testid={`time-proposal-slot-row-${i}`}>
                <div className="flex items-center gap-2">
                  <Input type="date" value={s.date} onChange={(e) => updateSlot(i, "date", e.target.value)} className="flex-1" data-testid={`time-proposal-slot-date-${i}`} />
                  <select value={s.hour} onChange={(e) => updateSlot(i, "hour", e.target.value)} className="border border-input rounded-md h-9 px-2 text-sm bg-background" data-testid={`time-proposal-slot-hour-${i}`}>
                    <option value="" disabled>HH</option>
                    {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="text-ink-soft">:</span>
                  <select value={s.minute} onChange={(e) => updateSlot(i, "minute", e.target.value)} className="border border-input rounded-md h-9 px-2 text-sm bg-background" data-testid={`time-proposal-slot-minute-${i}`}>
                    <option value="" disabled>MM</option>
                    {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <Input type="number" min={15} max={240} value={s.duration_minutes} onChange={(e) => updateSlot(i, "duration_minutes", e.target.value)} className="w-16" data-testid={`time-proposal-slot-duration-${i}`} />
                  <span className="text-xs text-ink-soft shrink-0">min</span>
                  {slots.length > 1 && (
                    <button onClick={() => removeSlot(i)} data-testid={`time-proposal-slot-remove-${i}`}><Trash2 size={14} className="text-ink/40 hover:text-terracotta" /></button>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <button onClick={() => setRightNow(i)} className="text-xs text-terracotta flex items-center gap-1 hover:underline" data-testid={`time-proposal-slot-right-now-${i}`}>
                    <Zap size={11} /> Right now
                  </button>
                  {preview && (
                    <p className="text-xs text-ink-soft" data-testid={`time-proposal-slot-preview-${i}`}>= {formatInTimezone(preview, "America/Sao_Paulo")} (São Paulo)</p>
                  )}
                </div>
              </div>
            );
          })}
          {slots.length < 5 && (
            <button onClick={addSlot} className="text-xs text-terracotta flex items-center gap-1" data-testid="time-proposal-add-slot-btn">
              <Plus size={12} /> Add another candidate time
            </button>
          )}
        </div>

        <Textarea placeholder="Optional note to the client…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="time-proposal-note-input" />

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-ink/60 px-4 py-2" data-testid="time-proposal-cancel-btn">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={createProposal.isPending}
            className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
            data-testid="time-proposal-submit-btn"
          >
            {createProposal.isPending ? "Sending…" : "Send to client"}
          </button>
        </div>
      </div>
    </div>
  );
}
